/**
 * useProfileSurveyForEmptyState
 *
 * Resolves the profile-survey "subject" for the empty-state card on the
 * renter home page and triggers a prefetch via dataVaultProfileSurveySlice
 * so the click → interview navigation is instant when the renter taps the
 * "Complete your profile" CTA.
 *
 * Subject identity:
 *   - Self mode: the logged-in user.
 *   - Proxy mode: a managed household member selected via the carousel.
 *     We walk `certifications` to find the first CertMember with
 *     `user_id === effectiveMemberId` and pull `member.user_id` (used as the
 *     proxy URL param) and `member.first_name` (used in the CTA copy).
 *
 * If the selected member can't be located in any cert (a defensive case
 * that shouldn't happen because the carousel itself is built from
 * `certifications`), we fall back to self mode rather than guess.
 *
 * Extracted from RenterHomePage to keep the page component readable.
 */
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AnyAction } from '@reduxjs/toolkit';
import type { ThunkDispatch } from 'redux-thunk';
import type { RootState } from 'src/redux/rootReducerType';
import type { Certification } from 'src/types/Certification';
import {
  fetchProfileSurvey,
  selectProfileSurvey,
} from 'src/redux/slices/DataVault/dataVaultProfileSurveySlice';

type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

interface UseProfileSurveyForEmptyStateArgs {
  certifications: Certification[];
  userId: number | undefined;
  effectiveMemberId: number | null;
  /** True when filteredActiveHouseholds.length === 0 — the only state in
   *  which we want to fetch and surface the profile-survey CTA. */
  isEmpty: boolean;
}

interface UseProfileSurveyForEmptyStateResult {
  surveyEntry: ReturnType<ReturnType<typeof selectProfileSurvey>> | undefined;
  targetUserId: number | null;
  actingOnMemberId: number | null;
  proxyMemberFirstName: string | undefined;
}

export function useProfileSurveyForEmptyState(
  args: UseProfileSurveyForEmptyStateArgs
): UseProfileSurveyForEmptyStateResult {
  const dispatch = useDispatch<AppDispatch>();
  const { certifications, userId, effectiveMemberId, isEmpty } = args;

  // Identify the proxy subject (CertMember row) if we're in proxy mode.
  // Iterate certifications looking for a cert_member whose `user_id`
  // matches the carousel-selected member; capture the first match's
  // user_id (used as the proxy URL param) and first_name. If none found,
  // treat as self mode.
  const proxyContext = useMemo<{
    actingOnMemberId: number | null;
    firstName: string | undefined;
  }>(() => {
    if (
      userId == null ||
      effectiveMemberId == null ||
      effectiveMemberId === userId
    ) {
      return { actingOnMemberId: null, firstName: undefined };
    }
    for (const cert of certifications || []) {
      const match = cert.cert_household?.cert_members?.find(
        (m) => m.user_id === effectiveMemberId
      );
      if (match) {
        return {
          actingOnMemberId: match.user_id,
          firstName: match.first_name,
        };
      }
    }
    // Defensive fallback — selected member not found anywhere → self mode.
    return { actingOnMemberId: null, firstName: undefined };
  }, [certifications, effectiveMemberId, userId]);

  // Subject of the profile survey: self user OR the proxy member's underlying user id.
  const targetUserId =
    proxyContext.actingOnMemberId != null ? effectiveMemberId : userId ?? null;

  const surveyEntry = useSelector(
    targetUserId != null
      ? selectProfileSurvey(targetUserId)
      : (): undefined => undefined
  );

  useEffect(() => {
    if (!isEmpty || targetUserId == null) {
      return;
    }
    dispatch(
      fetchProfileSurvey({
        targetUserId,
        actingOnMemberId: proxyContext.actingOnMemberId,
      })
    );
  }, [dispatch, isEmpty, targetUserId, proxyContext.actingOnMemberId]);

  return {
    surveyEntry,
    targetUserId,
    actingOnMemberId: proxyContext.actingOnMemberId,
    proxyMemberFirstName: proxyContext.firstName,
  };
}
